import Loading from "@components/loading/index.jsx";
import { Table } from "antd";
import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { formatMoney } from "@pages/profile-v2/utils";
import HistoryRangePicker from "@pages/profile-v2/pages/history-v2/components/RangePicker";
import NoContent from "@pages/profile-v2/pages/history-v2/components/NoContent";
import AccountInsightsErrorState from "../AccountInsightsErrorState";
import AccountInsightsIntervalToggle from "../AccountInsightsIntervalToggle";
import {
	ChartContainer,
	ChartLabel,
	ContentDivider,
	ControlsPanel,
	ControlsRow,
	InfiniteScrollSentinel,
	ScrollableTableBody,
	SectionCard,
	SectionHeader,
	SectionTitle,
	StyledTableWrapper,
	TableBadge,
	TableSection,
	TableTitle,
	TableTitleRow,
	TotalItem,
	TotalLabel,
	TotalsRow,
	TotalValue,
} from "./elements";
import useController from "./useController";

const AccountInsightsFinancialHistorySection = ({ translate }) => {
	const { state, handlers, infiniteScroll } = useController({ translate });

	const columns = useMemo(
		() => [
			{
				title: translate("accountInsights.financialHistory.date"),
				dataIndex: "date",
				key: "date",
				width: 110,
			},
			{
				title: translate("accountInsights.financialHistory.netLoss"),
				dataIndex: "gamingNetLoss",
				key: "gamingNetLoss",
				render: (value) => formatMoney(value, state.currency),
			},
			{
				title: translate("accountInsights.financialHistory.deposits"),
				dataIndex: "deposits",
				key: "deposits",
				render: (value) => formatMoney(value, state.currency),
			},
			{
				title: translate("accountInsights.financialHistory.withdrawals"),
				dataIndex: "withdrawals",
				key: "withdrawals",
				render: (value) => formatMoney(value, state.currency),
			},
			{
				title: translate("accountInsights.financialHistory.charge"),
				dataIndex: "charge",
				key: "charge",
				render: (value) => formatMoney(value, state.currency),
			},
			{
				title: translate("accountInsights.financialHistory.refund"),
				dataIndex: "refund",
				key: "refund",
				render: (value) => formatMoney(value, state.currency),
			},
			{
				title: translate("accountInsights.financialHistory.won"),
				dataIndex: "won",
				key: "won",
				render: (value) => formatMoney(value, state.currency),
			},
		],
		[state.currency, translate]
	);

	return (
		<SectionCard>
			<SectionHeader>
				<SectionTitle>{translate("accountInsights.financialHistory.title")}</SectionTitle>
				<ControlsPanel>
					<HistoryRangePicker
						translate={translate}
						onChange={handlers.setDateRange}
					/>
					<ControlsRow>
						<AccountInsightsIntervalToggle
							value={state.interval}
							onChange={handlers.setInterval}
							translate={translate}
						/>
					</ControlsRow>
				</ControlsPanel>
			</SectionHeader>

			{state.isLoading && <Loading loading />}

			{state.isError && !state.isLoading && (
				<AccountInsightsErrorState
					translate={translate}
					onRetry={handlers.refetch}
				/>
			)}

			{!state.isLoading && !state.isError && (
				<>
					{state.historyChart ? (
						<div>
							<ChartLabel>
								{translate("accountInsights.financialHistory.chartLabel")}
							</ChartLabel>
							<ChartContainer $height={320}>
								<Bar
									data={state.historyChart.data}
									options={state.historyChart.options}
								/>
							</ChartContainer>
						</div>
					) : (
						<NoContent
							translate={translate}
							noBackground
							title="accountInsights.empty.chartTitle"
							description="accountInsights.empty.chartDescription"
						/>
					)}

					{state.totals && (
						<TotalsRow>
							<TotalItem>
								<TotalLabel>
									{translate("accountInsights.financialHistory.totalNetLoss")}
								</TotalLabel>
								<TotalValue>
									{formatMoney(state.totals.gamingNetLoss, state.currency)}
								</TotalValue>
							</TotalItem>
							<TotalItem>
								<TotalLabel>
									{translate("accountInsights.financialHistory.totalDeposits")}
								</TotalLabel>
								<TotalValue>
									{formatMoney(state.totals.deposits, state.currency)}
								</TotalValue>
							</TotalItem>
							<TotalItem>
								<TotalLabel>
									{translate(
										"accountInsights.financialHistory.totalWithdrawals"
									)}
								</TotalLabel>
								<TotalValue>
									{formatMoney(state.totals.withdrawals, state.currency)}
								</TotalValue>
							</TotalItem>
						</TotalsRow>
					)}

					{state.buckets.length > 0 && <ContentDivider />}

					{state.buckets.length > 0 && (
						<TableSection>
							<TableTitleRow>
								<TableTitle>
									{translate("accountInsights.financialHistory.tableTitle")}
								</TableTitle>
								<TableBadge>
									{translate("accountInsights.table.periodCount", {
										count: state.buckets.length,
									})}
								</TableBadge>
							</TableTitleRow>
							<StyledTableWrapper>
								<ScrollableTableBody innerRef={infiniteScroll.scrollRootRef}>
									<Table
										columns={columns}
										dataSource={state.tableData}
										pagination={false}
										scroll={{ x: 920 }}
										size="middle"
									/>
									{state.hasMore && (
										<InfiniteScrollSentinel innerRef={infiniteScroll.sentinelRef}>
											{translate("accountInsights.table.scrollForMore")}
										</InfiniteScrollSentinel>
									)}
								</ScrollableTableBody>
							</StyledTableWrapper>
						</TableSection>
					)}
				</>
			)}
		</SectionCard>
	);
};

export default AccountInsightsFinancialHistorySection;
